import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPin, Disc3, ExternalLink, Users, Shuffle, Layers, Bell, BellOff, Star, Music, TrendingUp, Building2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ReleaseCard from "../components/ReleaseCard";
import ReleaseModal from "../components/ReleaseModal";
import PartnerSpot from "../components/PartnerSpot";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";

// ── Spotify Live Stats ────────────────────────────────────────────────────────
function SpotifyStats({ spotifyUrl }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!spotifyUrl) return;
    api.get(`/spotify/artist-stats?url=${encodeURIComponent(spotifyUrl)}`)
      .then(r => setStats(r.data))
      .catch(() => {});
  }, [spotifyUrl]);

  if (!stats) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 mt-3">
      {stats.followers > 0 && (
        <div className="flex items-center gap-1.5 bg-zinc-900/60 border border-zinc-800/50 rounded-lg px-3 py-1.5">
          <Users size={13} className="text-green-400" />
          <span className="text-xs text-zinc-300 font-medium">
            {stats.followers.toLocaleString("de-DE")}
          </span>
          <span className="text-xs text-zinc-500">Spotify Follower</span>
        </div>
      )}
      {stats.popularity > 0 && (
        <div className="flex items-center gap-1.5 bg-zinc-900/60 border border-zinc-800/50 rounded-lg px-3 py-1.5">
          <TrendingUp size={13} className="text-green-400" />
          <span className="text-xs text-zinc-300 font-medium">{stats.popularity}/100</span>
          <span className="text-xs text-zinc-500">Popularität</span>
        </div>
      )}
    </div>
  );
}

const SOCIAL_CFG = {
  instagram:  { label: "Instagram",  color: "#E1306C" },
  tiktok:     { label: "TikTok",     color: "#010101" },
  facebook:   { label: "Facebook",   color: "#1877F2" },
  youtube:    { label: "YouTube",    color: "#FF0000" },
  soundcloud: { label: "SoundCloud", color: "#FF5500" },
  spotify:    { label: "Spotify",    color: "#1DB954" },
  beatport:   { label: "Beatport",   color: "#FF6F00" },
  twitch:     { label: "Twitch",     color: "#9146FF" },
};

/* Deterministic accent color from artist name */
const ACCENT_COLORS = [
  ["#e11d48", "#be123c"],   // rose
  ["#f97316", "#c2410c"],   // orange
  ["#a855f7", "#7e22ce"],   // purple
  ["#06b6d4", "#0e7490"],   // cyan
  ["#10b981", "#065f46"],   // emerald
  ["#f59e0b", "#b45309"],   // amber
  ["#3b82f6", "#1d4ed8"],   // blue
];

function getAccent(name = "") {
  const i = name.split("").reduce((s, c) => s + c.charCodeAt(0), 0) % ACCENT_COLORS.length;
  return ACCENT_COLORS[i];
}

function SocialLinks({ links }) {
  if (!links) return null;
  const items = Object.entries(SOCIAL_CFG).filter(([k]) => links[k]);
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {items.map(([key, cfg]) => (
        <a
          key={key}
          href={links[key]}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold hover:opacity-80 transition-opacity shadow-md"
          style={{ background: cfg.color }}
        >
          {cfg.label} <ExternalLink size={9} />
        </a>
      ))}
    </div>
  );
}

function ReleaseListItem({ release, onClick }) {
  const year = release.erscheinungsdatum?.slice(0, 4);
  return (
    <button
      onClick={() => onClick(release)}
      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.04] transition-colors text-left group"
      data-testid={`release-list-item-${release.id}`}
    >
      <div className="w-10 h-10 rounded overflow-hidden bg-zinc-800 flex-shrink-0">
        {release.cover_url ? (
          <img src={release.cover_url} alt={release.titel} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Disc3 size={16} className="text-zinc-600" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-zinc-200 text-sm font-medium truncate group-hover:text-white transition-colors">{release.titel}</p>
        <p className="text-zinc-500 text-xs truncate">{release.interpret}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {year && <span className="text-zinc-600 text-xs">{year}</span>}
        {release.typ && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.04] text-zinc-500">
            {release.typ}
          </span>
        )}
      </div>
    </button>
  );
}

function LoadingPage() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      <div className="flex items-center justify-center h-72">
        <div className="w-8 h-8 border-2 border-rose-600 border-t-transparent rounded-full animate-spin" />
      </div>
      <Footer />
    </div>
  );
}

const RELATED_LIMIT = 4;

function RelatedArtistsSection({ artists }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? artists : artists.slice(0, RELATED_LIMIT);
  const hidden  = artists.length - RELATED_LIMIT;

  return (
    <section className="pb-10">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-px flex-1 bg-white/5" />
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 flex-shrink-0">
            Häufig gemeinsam mit
          </p>
          <div className="h-px flex-1 bg-white/5" />
        </div>
        <div className="flex flex-wrap gap-3">
          {visible.map((a) => (
            <Link
              key={a.slug}
              to={`/artist/${a.slug}`}
              className="group flex items-center gap-2.5 px-3 py-2 rounded-2xl bg-zinc-900/40 hover:bg-zinc-900/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] transition-all duration-200"
              data-testid={`related-artist-${a.slug}`}
            >
              <div className="w-8 h-8 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 ring-1 ring-white/10">
                {a.bild_url ? (
                  <img src={a.bild_url} alt={a.name} className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-xs font-black text-white"
                    style={{ background: `linear-gradient(135deg, ${getAccent(a.name)[0]}60, ${getAccent(a.name)[1]}40)` }}
                  >
                    {a.name[0]}
                  </div>
                )}
              </div>
              <span className="text-zinc-400 group-hover:text-white text-sm font-medium transition-colors whitespace-nowrap">
                {a.name}
              </span>
            </Link>
          ))}
          {/* Expand / collapse toggle */}
          {!expanded && hidden > 0 && (
            <button
              onClick={() => setExpanded(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-zinc-900/30 hover:bg-zinc-900/60 text-zinc-500 hover:text-zinc-300 text-sm font-semibold transition-all duration-200"
            >
              +{hidden} weitere
            </button>
          )}
          {expanded && hidden > 0 && (
            <button
              onClick={() => setExpanded(false)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-2xl bg-zinc-900/30 hover:bg-zinc-900/60 text-zinc-600 hover:text-zinc-400 text-sm transition-all duration-200"
            >
              Weniger
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      <Header />
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-24">
        <p className="text-rose-500 text-xs font-bold uppercase tracking-widest mb-3">404</p>
        <h1 className="text-3xl font-black text-white font-heading mb-3">Artist nicht gefunden</h1>
        <p className="text-zinc-500 text-sm mb-6">Dieser Artist existiert noch nicht in unserer Datenbank.</p>
        <Link to="/" className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold rounded-lg transition-colors">
          Zur Startseite
        </Link>
      </div>
      <Footer />
    </div>
  );
}

export default function ArtistPage() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const { publicUser, isPublicAuth, login, publicApi } = usePublicAuth();
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/artists/${slug}`)
      .then(r => {
        setArtist(r.data);
        document.title = `${r.data.name} – Artists | HUWND`;
      })
      .catch(err => { if (err.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  // Load follow status when user is logged in
  useEffect(() => {
    if (!isPublicAuth || !artist?.id) return;
    publicApi("get", "/me/follows")
      .then(res => {
        setFollowing(res.data.some(f => f.artist_id === artist.id));
      })
      .catch(() => {});
  }, [isPublicAuth, artist?.id, publicApi]);

  const handleFollow = async () => {
    if (!isPublicAuth) { login(); return; }
    if (!publicUser?.is_supporter) return;
    setFollowLoading(true);
    try {
      if (following) {
        await publicApi("delete", `/me/follows/${artist.id}`);
        setFollowing(false);
      } else {
        await publicApi("post", `/me/follows/${artist.id}`);
        setFollowing(true);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Fehler");
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) return <LoadingPage />;
  if (notFound) return <NotFoundPage />;
  if (!artist) return null;

  const SITE = "https://handsupwillneverdie.de";
  const accentFrom = "#3b82f6";
  const accentTo = "#1d4ed8";
  const pageTitle = `${artist.name} – Artist | HUWND`;
  const pageDesc = artist.bio
    ? artist.bio.slice(0, 155) + (artist.bio.length > 155 ? "…" : "")
    : `${artist.name} auf Hands Up Will Never Die – ${artist.release_count || 0} Releases.`;
  const canonicalUrl = `${SITE}/artist/${artist.slug}`;
  const artistJsonLd = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "MusicGroup",
    "name": artist.name,
    "url": canonicalUrl,
    ...(artist.bild_url ? { "image": artist.bild_url } : {}),
    ...(artist.bio ? { "description": artist.bio.slice(0, 500) } : {}),
  });

  return (
    <div className="min-h-screen bg-[#050505]">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="profile" />
        <meta property="og:site_name" content="HUWND – Hands Up Will Never Die" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        {artist.bild_url && <meta property="og:image" content={artist.bild_url} />}
        <meta name="twitter:card" content={artist.bild_url ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        {artist.bild_url && <meta name="twitter:image" content={artist.bild_url} />}
        <script type="application/ld+json">{artistJsonLd}</script>
      </Helmet>
      <Header />

      {/* ── HERO ── */}
      <section className="relative pt-24 pb-0 overflow-hidden">
        {/* Background blur layer (nur wenn kein Banner) */}
        {!artist.banner_url && artist.bild_url && (
          <div className="absolute inset-0 pointer-events-none">
            <img src={artist.bild_url} alt="" className="w-full h-full object-cover scale-110 blur-3xl opacity-[0.08]" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/85 to-[#050505]" />
          </div>
        )}

        {/* Accent color bar */}
        <div
          className="absolute top-0 left-0 right-0 h-1 pointer-events-none"
          style={{ background: `linear-gradient(90deg, ${accentFrom}, ${accentTo}, transparent)` }}
        />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/artists"
            className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-white text-sm mb-6 transition-colors group"
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
            Artists
          </Link>

          {/* ── Banner (eigenes Bild ODER Gradient-Default) ── */}
          <div
            className="relative w-full rounded-2xl overflow-hidden mb-0"
            style={{
              height: "clamp(140px, 22vw, 220px)",
              boxShadow: "0 4px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)",
            }}
          >
            {artist.banner_url ? (
              <img
                src={artist.banner_url}
                alt=""
                className="w-full h-full object-cover"
                style={{ objectPosition: "center center" }}
              />
            ) : (
              /* ── Default: 3D-Gradient Banner (wie Artist Studio Card) ── */
              <div className="w-full h-full relative">
                {/* Basis-Gradient – Deep Blue */}
                <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, #0c1a2e 0%, #0f2241 55%, #080f1c 100%)" }} />

                {/* Radiale Lichtpunkte für 3D-Tiefe */}
                <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 90% at 18% 50%, rgba(59,130,246,0.28) 0%, transparent 65%), radial-gradient(ellipse 40% 70% at 72% 20%, rgba(99,102,241,0.18) 0%, transparent 60%), radial-gradient(ellipse 35% 55% at 88% 80%, rgba(37,99,235,0.14) 0%, transparent 55%)" }} />

                {/* Subtiles Grid-Mesh */}
                <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "linear-gradient(rgba(96,165,250,1) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,1) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />

                {/* Inset Top-Highlight (3D-Kante) */}
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent 5%, rgba(59,130,246,0.5) 30%, rgba(147,197,253,0.35) 55%, rgba(99,102,241,0.3) 75%, transparent 95%)" }} />

                {/* Vinyl-Schallplatte (dekorativ, rechts halb sichtbar) */}
                <div className="absolute top-1/2 -translate-y-1/2 opacity-[0.28]"
                  style={{ right: "-70px", width: "clamp(170px,25vw,240px)", aspectRatio: "1" }}>
                  <svg viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="100" cy="100" r="98" fill="#060d1a" stroke="rgba(96,165,250,0.35)" strokeWidth="0.8"/>
                    {[88,80,73,66,59,52,45].map((r, i) => (
                      <circle key={i} cx="100" cy="100" r={r} fill="none" stroke="rgba(96,165,250,0.14)" strokeWidth="0.8"/>
                    ))}
                    <circle cx="100" cy="100" r="32" fill="rgba(12,26,46,0.95)" stroke="rgba(96,165,250,0.4)" strokeWidth="1"/>
                    <circle cx="100" cy="100" r="26" fill="none" stroke="rgba(96,165,250,0.18)" strokeWidth="0.6"/>
                    <circle cx="100" cy="100" r="5.5" fill="#030810" stroke="rgba(96,165,250,0.5)" strokeWidth="0.8"/>
                    <ellipse cx="72" cy="66" rx="20" ry="11" fill="rgba(255,255,255,0.05)" transform="rotate(-35 72 66)"/>
                    <ellipse cx="130" cy="140" rx="14" ry="7" fill="rgba(59,130,246,0.06)" transform="rotate(-35 130 140)"/>
                  </svg>
                </div>

                {/* Horizontal scan-line Akzent */}
                <div className="absolute left-0 right-0" style={{ top: "38%", height: "1px", background: "linear-gradient(90deg, transparent 0%, rgba(59,130,246,0.2) 20%, rgba(59,130,246,0.1) 80%, transparent 100%)" }} />
              </div>
            )}
            {/* Gradient unten für Avatar-Overlap */}
            <div className="absolute inset-0"
              style={{ background: "linear-gradient(to bottom, transparent 35%, rgba(5,5,5,0.75) 100%)" }} />
          </div>

          {/* ── Profil-Zeile: Avatar + Info (Avatar überlappt Banner) ── */}
          <div className="flex flex-col sm:flex-row items-start gap-6 sm:gap-8 pb-12 mt-[-48px]">
            {/* Avatar */}
            <div className="relative flex-shrink-0 z-10">
              <div
                className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl overflow-hidden"
                style={{
                  boxShadow: `0 0 0 3px #050505, 0 0 0 4px ${accentFrom}30, 0 0 40px ${accentFrom}25, 0 20px 60px rgba(0,0,0,0.7)`,
                  border: `1px solid rgba(255,255,255,0.08)`,
                }}
              >
                {artist.bild_url ? (
                  <img src={artist.bild_url} alt={artist.name} className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-5xl sm:text-6xl font-black text-white font-heading"
                    style={{ background: `linear-gradient(135deg, ${accentFrom}40, ${accentTo}20)`, border: `1px solid ${accentFrom}30` }}
                  >
                    {artist.name[0]}
                  </div>
                )}
              </div>
              {/* Accent glow ring */}
              <div
                className="absolute -inset-1 rounded-2xl -z-10 opacity-25 blur-sm"
                style={{ background: `linear-gradient(135deg, ${accentFrom}, ${accentTo})` }}
              />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0 pt-2 sm:pt-14">

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white font-heading leading-none mb-4 tracking-tight flex items-center gap-3 flex-wrap">
                {artist.name}
                {artist.linked_user_ids?.length > 0 && (
                  <span className="relative group cursor-help inline-flex flex-shrink-0 self-center">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{
                        background: "linear-gradient(145deg, #60a5fa 0%, #3b82f6 45%, #1d4ed8 100%)",
                        boxShadow: "0 1px 0 rgba(255,255,255,0.35) inset, 0 -1px 0 rgba(0,0,0,0.2) inset, 0 3px 12px rgba(59,130,246,0.55), 0 1px 3px rgba(0,0,0,0.3)",
                      }}>
                      <svg viewBox="0 0 12 12" fill="none" className="w-3.5 h-3.5">
                        <path d="M2 6.5L4.5 9L10 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2.5 w-56 px-3 py-2 text-xs text-zinc-200 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none text-center z-50 leading-relaxed font-normal whitespace-normal tracking-normal"
                      style={{ background: "rgba(10,10,10,0.97)", border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 8px 24px rgba(0,0,0,0.6)" }}>
                      {t("artist_page.managed_tooltip")}
                    </span>
                  </span>
                )}
                <span
                  className="text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full self-center"
                  style={{ background: `${accentFrom}20`, color: accentFrom, border: `1px solid ${accentFrom}30` }}
                >
                  Artist
                </span>

                {/* Follow-Button – inline neben dem ARTIST-Pill */}
                {publicUser?.is_supporter ? (
                  <button
                    onClick={handleFollow}
                    disabled={followLoading}
                    data-testid="follow-artist-btn"
                    className={`self-center flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold tracking-normal transition-all duration-200 ${
                      following
                        ? "bg-rose-600/15 text-rose-400 shadow-[inset_0_1px_0_rgba(225,29,72,0.1)] hover:bg-rose-600/25"
                        : "bg-white/[0.06] text-zinc-300 hover:text-white hover:bg-white/[0.10] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]"
                    }`}
                  >
                    {following
                      ? <><BellOff size={14} /> Nicht mehr folgen</>
                      : <><Bell size={14} /> Folgen & benachrichtigen</>
                    }
                  </button>
                ) : (
                  <Link
                    to="/supporter"
                    data-testid="follow-supporter-hint"
                    className="self-center flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold tracking-normal bg-white/[0.04] text-zinc-400 hover:text-zinc-200 hover:bg-white/[0.07] transition-all duration-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]"
                  >
                    <Star size={13} className="text-rose-500" /> Supporter werden
                  </Link>
                )}
              </h1>

              {/* Stats row */}
              <div className="flex flex-wrap items-center gap-5 text-sm">
                {artist.wohnort && (
                  <span className="flex items-center gap-1.5 text-zinc-400">
                    <MapPin size={13} style={{ color: accentFrom }} />
                    {artist.wohnort}
                  </span>
                )}
                {artist.releases?.length > 0 && (
                  <span className="flex items-center gap-1.5 text-zinc-400">
                    <Disc3 size={13} style={{ color: accentFrom }} />
                    <span className="font-semibold text-white">{artist.releases.length}</span> Releases
                  </span>
                )}
                {artist.related_artists?.length > 0 && (
                  <span className="flex items-center gap-1.5 text-zinc-400">
                    <Users size={13} style={{ color: accentFrom }} />
                    <span className="font-semibold text-white">{artist.related_artists.length}</span> Kollaborationen
                  </span>
                )}
                {artist.remixes?.length > 0 && (
                  <span className="flex items-center gap-1.5 text-zinc-400">
                    <Shuffle size={13} style={{ color: accentFrom }} />
                    <span className="font-semibold text-white">{artist.remixes.length}</span> Remixes
                  </span>
                )}
                {artist.compilations?.length > 0 && (
                  <span className="flex items-center gap-1.5 text-zinc-400">
                    <Layers size={13} style={{ color: accentFrom }} />
                    <span className="font-semibold text-white">{artist.compilations.length}</span> Kompilationen
                  </span>
                )}
              </div>

              {/* Label Badge */}
              {artist.label && (
                <Link
                  to={`/label/${artist.label.slug}`}
                  data-testid="artist-label-badge"
                  className="inline-flex items-center gap-2 mt-1 group w-fit"
                >
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all"
                    style={{
                      background: "rgba(16,185,129,0.08)",
                      border: "1px solid rgba(16,185,129,0.2)",
                      boxShadow: "0 1px 0 rgba(52,211,153,0.06) inset",
                    }}>
                    {artist.label.logo_url ? (
                      <img src={artist.label.logo_url} alt="" className="w-5 h-5 rounded-md object-cover flex-shrink-0" />
                    ) : (
                      <Building2 size={13} className="text-emerald-500 flex-shrink-0" />
                    )}
                    <span className="text-xs font-semibold text-emerald-400 group-hover:text-emerald-300 transition-colors">
                      {artist.label.name}
                    </span>
                  </div>
                </Link>
              )}

              {artist.social_links && Object.keys(SOCIAL_CFG).some(k => artist.social_links[k]) && (
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-500 mt-5">
                  {t("artist_page.social_media_channels")}
                </p>
              )}
              <SocialLinks links={artist.social_links} />
            </div>
          </div>
        </div>

        {/* Gradient fade to content */}
        <div className="h-12 bg-gradient-to-b from-transparent to-[#050505]" />
      </section>

      {/* ── TOP TRACKS ── */}
      {artist.top_tracks?.length > 0 && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline gap-3 mb-5">
              <h2 className="text-2xl font-black text-white font-heading flex items-center gap-2.5">
                <Music size={18} style={{ color: accentFrom }} />
                {artist.name.endsWith("s") || artist.name.endsWith("x") || artist.name.endsWith("z") ? `${artist.name}'` : `${artist.name}s`} persönliche Top 5
              </h2>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ background: `${accentFrom}20`, color: accentFrom }}
              >
                {artist.top_tracks.length}
              </span>
            </div>
            <div className="rounded-2xl bg-zinc-900/30 overflow-hidden divide-y divide-white/[0.03] shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
              {artist.top_tracks.map((track, i) => (
                <button
                  key={track.id}
                  onClick={() => setSelectedRelease(track)}
                  data-testid={`top-track-${i + 1}`}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/[0.04] transition-colors text-left group"
                >
                  <span className="text-zinc-700 font-black text-sm w-5 text-center flex-shrink-0 tabular-nums">{i + 1}</span>
                  <div className="w-10 h-10 rounded-xl overflow-hidden bg-zinc-800 flex-shrink-0 shadow-sm">
                    {track.cover_url ? (
                      <img src={track.cover_url} alt={track.titel} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music size={14} className="text-zinc-600" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-zinc-200 text-sm font-semibold truncate group-hover:text-white transition-colors">{track.interpret}</p>
                    <p className="text-zinc-500 text-xs truncate">{track.titel}</p>
                  </div>
                  {track.erscheinungsdatum && (
                    <span className="text-zinc-700 text-xs flex-shrink-0 hidden sm:block tabular-nums">{track.erscheinungsdatum.slice(0, 4)}</span>
                  )}
                  {track.typ && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/[0.04] text-zinc-500 flex-shrink-0 hidden sm:block">
                      {track.typ}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── BIO ── */}
      {artist.biografie && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-600 mb-4">
              {t("artist_page.about_heading", { name: artist.name })}
            </h2>
            <p className="text-zinc-400 text-sm sm:text-base leading-[1.9] whitespace-pre-line max-w-3xl">
              {artist.biografie}
            </p>
          </div>
        </section>
      )}

      {/* ── ALIASES / PSEUDONYME ── */}
      {artist.aliases?.length > 0 && (
        <section className="pb-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px flex-1 bg-white/5" />
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 flex-shrink-0">
                Weitere Pseudonyme & Künstlernamen
              </p>
              <div className="h-px flex-1 bg-white/5" />
            </div>
            <div className="flex flex-wrap gap-3">
              {artist.aliases.map((a) => (
                <Link
                  key={a.id}
                  to={`/artist/${a.slug}`}
                  className="group flex items-center gap-2.5 px-3 py-2 rounded-2xl bg-zinc-900/40 hover:bg-zinc-900/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all duration-200"
                  data-testid={`alias-artist-${a.slug}`}
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 ring-1 ring-white/10">
                    {a.bild_url ? (
                      <img src={a.bild_url} alt={a.name} className="w-full h-full object-cover" />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-xs font-black text-white"
                        style={{ background: `linear-gradient(135deg, ${getAccent(a.name)[0]}60, ${getAccent(a.name)[1]}40)` }}
                      >
                        {a.name[0]}
                      </div>
                    )}
                  </div>
                  <span className="text-zinc-400 group-hover:text-white text-sm font-medium transition-colors whitespace-nowrap">
                    {a.name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── RELATED ARTISTS ── */}
      {artist.related_artists?.length > 0 && (
        <RelatedArtistsSection artists={artist.related_artists} />
      )}

      {/* ── DISCOGRAPHY ── */}
      {artist.releases?.length > 0 && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline gap-3 mb-6">
              <h2 className="text-2xl font-black text-white font-heading">Diskografie</h2>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ background: `${accentFrom}20`, color: accentFrom }}
              >
                {artist.releases.length}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {artist.releases.map(r => (
                <ReleaseCard key={r.id} release={r} onClick={setSelectedRelease} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── REMIXES ── */}
      {artist.remixes?.length > 0 && (
        <section className="pb-12" data-testid="remixes-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline gap-3 mb-4">
              <h2 className="text-xl font-black text-white font-heading flex items-center gap-2">
                <Shuffle size={18} style={{ color: accentFrom }} /> Remixes
              </h2>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ background: `${accentFrom}20`, color: accentFrom }}
              >
                {artist.remixes.length}
              </span>
            </div>
            <div className="rounded-2xl bg-zinc-900/30 overflow-hidden divide-y divide-white/[0.03] shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
              {artist.remixes.map(r => (
                <ReleaseListItem key={r.id} release={r} onClick={setSelectedRelease} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── COMPILATIONS ── */}
      {artist.compilations?.length > 0 && (
        <section className="pb-12" data-testid="compilations-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-baseline gap-3 mb-4">
              <h2 className="text-xl font-black text-white font-heading flex items-center gap-2">
                <Layers size={18} style={{ color: accentFrom }} /> Auf Kompilationen
              </h2>
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ background: `${accentFrom}20`, color: accentFrom }}
              >
                {artist.compilations.length}
              </span>
            </div>
            <div className="rounded-2xl bg-zinc-900/30 overflow-hidden divide-y divide-white/[0.03] shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]">
              {artist.compilations.map(r => (
                <ReleaseListItem key={r.id} release={r} onClick={setSelectedRelease} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── PARTNER SPOT ── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <PartnerSpot position="artist" />
      </div>

      <Footer />

      {selectedRelease && (
        <ReleaseModal release={selectedRelease} onClose={() => setSelectedRelease(null)} />
      )}
    </div>
  );
}
